/**
 * A mould label, big enough to actually scan.
 *
 * The code on the mould's record is small by necessity - it shares a row with the
 * photo and the specification. That size is fine as a visual reference and
 * useless as something to point a phone at, which is why scanning "did not
 * work": people were aiming at a 104-pixel square on a monitor.
 *
 * This shows the same code large, and prints it, so the label can go on the tool
 * rather than being read off a screen at all.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { mouldQrValue } from '../../lib/qr.js';
import { Printer } from 'lucide-react';
import { Button } from '../../components/ui/button.js';
import { Dialog } from '../../components/ui/dialog.js';

/** Marks the body so the print stylesheet knows to hide everything else. */
const PRINTING_CLASS = 'mt-printing-label';

interface LabelProps {
  /** The mould's id - what the QR opens. */
  mouldId: string;
  assetCode: string;
  name: string;
  owner?: string | null;
}

/** The label itself. Rendered twice - once on screen, once for the printer. */
function Label({ mouldId, assetCode, name, owner }: LabelProps) {
  return (
    <div className="flex max-w-full flex-col items-center gap-3 rounded-lg border border-border bg-white p-5">
      {/*
        Fixed white ground and black modules regardless of theme - a QR inverted
        by dark mode is not readable by most scanners.

        No width or height classes on the SVG. `h-auto` collapsed it to zero
        height under print media, which printed the caption and no code at all.
        Left alone, its own 320px attributes govern and both media agree.
      */}
      <QRCodeSVG value={mouldQrValue({ id: mouldId })} level="M" size={320} bgColor="#ffffff" fgColor="#000000" />
      <div className="w-full text-center text-black">
        <p className="font-mono text-2xl font-bold tracking-tight">{assetCode}</p>
        <p className="mt-0.5 text-sm font-medium">{name}</p>
        {owner ? <p className="mt-0.5 text-xs">{owner}</p> : null}
      </div>
    </div>
  );
}

export function MouldLabelDialog({
  mouldId,
  assetCode,
  name,
  owner,
  open,
  onOpenChange,
}: LabelProps & { open: boolean; onOpenChange: (open: boolean) => void }) {
  // The class has to come off again whatever ends the print - closing the
  // dialog, or cancelling the browser's own print dialog.
  useEffect(() => {
    if (!open) document.body.classList.remove(PRINTING_CLASS);
    return () => document.body.classList.remove(PRINTING_CLASS);
  }, [open]);

  function print() {
    document.body.classList.add(PRINTING_CLASS);
    // Let the class land before the browser snapshots the page.
    requestAnimationFrame(() => {
      window.print();
      document.body.classList.remove(PRINTING_CLASS);
    });
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        size="sm"
        title="Mould label"
        description="Print this and fix it to the tool, or scan it straight off the screen."
        footer={
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={print}>
              <Printer className="size-4" /> Print
            </Button>
          </>
        }
      >
        <Label mouldId={mouldId} assetCode={assetCode} name={name} owner={owner} />
      </Dialog>

      {/*
        The printed copy lives directly on <body>, not inside the dialog.
        Printing the dialog copy meant printing something nested in a flex column
        with an overflow-y scroll container, and those ancestors collapse once the
        print rules take hold - the QR came out zero pixels tall while its caption
        printed fine. At body level it has no ancestors left to collapse.
      */}
      {open
        ? createPortal(
            <div className="mt-print-label" aria-hidden>
              <Label mouldId={mouldId} assetCode={assetCode} name={name} owner={owner} />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
